import React, { FunctionComponent } from 'react';
import { useMediaQuery } from 'react-responsive';
import styles from './Enroll.module.scss';
import { useNavigate, Link } from 'react-router-dom';
import { ServicesPhoto } from 'assets/session/dashboard/index';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { SessionState, setUser } from 'features/session/sessionSlice';
import ExpertCard from './cards/ExpertCard';
import Text from 'common/components/Text/Text';
import { VisaCard } from 'assets/explore/index';
import { navigateTop } from 'common/utils';
import CollapseCard from 'common/components/CollapseCard/CollapseCard';
import { useFormik } from 'formik';
import { useUpdateUserMutation } from 'services/exploreApi';

interface EnrollChecks {
  enrollCAS: string | null;
  enrollOfferLetter: string | null;
  enrollPassport: string | null;
  enrollPayment: string | null;
  enrollBankLetter: string | null;
}

const Enroll: FunctionComponent = () => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(({ session }: { session: SessionState }) => session);
  const [updateUser, { data: uploadedUser, isLoading }] = useUpdateUserMutation();

  const handleFormik = (field: string, value: boolean) => {
    formik.setFieldValue(field, String(value));
  };

  const formik = useFormik<EnrollChecks>({
    initialValues: {
      enrollCAS: null,
      enrollOfferLetter: null,
      enrollPassport: null,
      enrollPayment: null,
      enrollBankLetter: null,
    },
    onSubmit: async (enrollChecks: EnrollChecks) => {
      const formData = new FormData();
      if (user) {
        if (enrollChecks.enrollCAS) {
          formData.append('user[enroll_cas]', enrollChecks.enrollCAS);
        }
        if (enrollChecks.enrollOfferLetter) {
          formData.append('user[enroll_offer_letter]', enrollChecks.enrollOfferLetter);
        }
        if (enrollChecks.enrollPassport) {
          formData.append('user[enroll_passport]', enrollChecks.enrollPassport);
        }
        if (enrollChecks.enrollPayment) {
          formData.append('user[enroll_payment]', enrollChecks.enrollPayment);
        }
        if (enrollChecks.enrollBankLetter) {
          formData.append('user[enroll_bank_letter]', enrollChecks.enrollBankLetter);
        }
        await updateUser({ data: formData, id: user.id })
          .unwrap()
          .then((res) => {
            dispatch(setUser({ ...res.data, accessToken: user?.accessToken }));
            navigate('/home');
          });
      }
    },
  });
  return (
    <div className={styles.container}>
      <div className={styles.leftColumn}>
        <div className={styles.header}>
          {!isMobile && (
            <div className={styles.headerFirstLine}>
              <div className={styles.ubicationText}>
                <Link to=".." relative="path">
                  <Text size={18} weight={500}>
                    {'Dashboard >'}
                  </Text>
                </Link>
                <Link to=".." relative="path">
                  <Text size={18} weight={500}>
                    {'Enroll >'}
                  </Text>
                </Link>
                <Link to=".." relative="path">
                  <Text size={18} weight={400}>
                    {'Visa Checklist'}
                  </Text>
                </Link>
              </div>
              <button
                className={`${styles.exitButton} btn`}
                type="submit"
                form="enrollForm"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className={`spinner-border ${styles.loading}`} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  <Text size={16} weight={700}>
                    Save & Leave
                  </Text>
                )}
              </button>
            </div>
          )}
          {isMobile && <i className="bi bi-chevron-left" onClick={() => navigate('/home')}></i>}
          <Text size={isMobile ? 20 : 24} weight={700}>
            Visa checklist
          </Text>
          {isMobile && <img src={VisaCard} alt="Visa Card img" className={styles.visaCardImg} />}
          {!isMobile && (
            <Text
              size={14}
              weight={400}
              inlineStyle={{ marginTop: '15px', lineHeight: '24px', marginBottom: '45px' }}
            >
              Make sure you got all the documents in place to get your student visa.
            </Text>
          )}
        </div>
        <div className={styles.bodyContainer}>
          {isMobile && (
            <div>
              <Text size={14} weight={600} inlineStyle={{ marginBottom: '16px' }}>
                What is Visa?
              </Text>
              <Text size={14} weight={400} inlineStyle={{ marginBottom: '36px' }}>
                Make sure you got all the documents in place to get your student visa.
              </Text>
              <Text size={14} weight={600} inlineStyle={{ marginBottom: '16px' }}>
                Checklist
              </Text>
            </div>
          )}
          <form className={styles.cardFlex} id="enrollForm" onSubmit={formik.handleSubmit}>
            <CollapseCard
              name="CAS"
              status={user?.enroll_cas ? 'active' : 'pending'}
              button="check"
              buttonValue="enrollCAS"
              handleFormik={handleFormik}
            />
            <CollapseCard
              name="Offer Letter"
              status={user?.enroll_offer_letter ? 'active' : 'pending'}
              button="check"
              buttonValue="enrollOfferLetter"
              handleFormik={handleFormik}
            />
            <CollapseCard
              name="Passport"
              status={user?.enroll_passport ? 'active' : 'pending'}
              button="check"
              buttonValue="enrollPassport"
              handleFormik={handleFormik}
            />
            <CollapseCard
              name="Payment"
              status={user?.enroll_payment ? 'active' : 'pending'}
              button="check"
              buttonValue="enrollPayment"
              handleFormik={handleFormik}
            />
            <CollapseCard
              name="Bank letter"
              status={user?.enroll_bank_letter ? 'active' : 'pending'}
              button="check"
              buttonValue="enrollBankLetter"
              handleFormik={handleFormik}
            />
          </form>
          {isMobile && (
            <button
              className={styles.saveButton}
              type="submit"
              form="enrollForm"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className={`spinner-border ${styles.loading}`} role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <Text size={16} weight={700}>
                  Save & Leave
                </Text>
              )}
            </button>
          )}
        </div>
      </div>
      {!isMobile && (
        <div className={styles.rightColumn}>
          <div className={styles.item}>
            <Text size={20} weight={600}>
              My expert
            </Text>
            <ExpertCard />
          </div>
          <div className={styles.item}>
            <div className={styles.servicesBox}>
              <img src={ServicesPhoto} className={styles.image}></img>
              <Text size={16} weight={500} inlineStyle={{ width: '80%', textAlign: 'center' }}>
                Our trusted partners help you maximize your chances
              </Text>
              <div className={styles.button}>Discover services</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Enroll;
